import React, { Children, isValidElement, useEffect, useId } from "react";
import { createPortal } from "react-dom";
import AppDialogBody from "./AppDialogBody";

export const AppDialogContext = React.createContext({
  titleId: undefined,
  showCloseButton: true,
  onClose: undefined,
});

function getDialogZone(element) {
  if (!isValidElement(element)) {
    return null;
  }

  return element.type?.appDialogZone ?? null;
}

export default function AppDialog({
  open = false,
  inline = false,
  size = "md",
  showCloseButton = true,
  onClose,
  className = "",
  rootClassName = "",
  children,
}) {
  const titleId = useId();

  useEffect(() => {
    if (!open || inline || !onClose) {
      return undefined;
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        onClose(event);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [inline, onClose, open]);

  const headerNodes = [];
  const bodyNodes = [];
  const footerNodes = [];
  const normalizedChildren = Children.toArray(children);

  normalizedChildren.forEach((child, index) => {
    const zone = getDialogZone(child);

    if (zone === "header") {
      headerNodes.push(React.cloneElement(child, { key: child.key ?? `header-${index}` }));
      return;
    }

    if (zone === "footer") {
      footerNodes.push(React.cloneElement(child, { key: child.key ?? `footer-${index}` }));
      return;
    }

    if (zone === "body") {
      bodyNodes.push(React.cloneElement(child, { key: child.key ?? `body-${index}` }));
      return;
    }

    if (child != null) {
      bodyNodes.push(
        <AppDialogBody key={`body-fallback-${index}`}>
          {child}
        </AppDialogBody>
      );
    }
  });

  const rootClasses = [
    "ds-dialog-root",
    inline ? "ds-dialog-root--inline" : "",
    rootClassName,
  ]
    .filter(Boolean)
    .join(" ");

  const hasHeader = headerNodes.length > 0;
  const hasFooter = footerNodes.length > 0;

  const dialogClasses = [
    "ds-dialog",
    className,
    size === "sm" ? "ds-dialog--sm" : "",
    size === "md" ? "ds-dialog--md" : "",
    size === "lg" ? "ds-dialog--lg" : "",
    size === "xl" ? "ds-dialog--xl" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const contentClasses = [
    "ds-dialog__content",
    hasHeader ? "ds-dialog__content--with-header" : "ds-dialog__content--without-header",
    hasFooter ? "ds-dialog__content--with-footer" : "ds-dialog__content--without-footer",
  ]
    .filter(Boolean)
    .join(" ");

  if (!open) {
    return null;
  }

  const dialogNode = (
    <div className={rootClasses}>
      {!inline ? <div className="ds-dialog-backdrop" aria-hidden="true" /> : null}
      <AppDialogContext.Provider value={{ titleId, showCloseButton, onClose }}>
        <section
          className={dialogClasses}
          role="dialog"
          aria-modal={inline ? undefined : "true"}
          aria-labelledby={hasHeader ? titleId : undefined}
        >
          {headerNodes}
          {bodyNodes.length > 0 ? <div className={contentClasses}>{bodyNodes}</div> : null}
          {footerNodes}
        </section>
      </AppDialogContext.Provider>
    </div>
  );

  if (inline || typeof document === "undefined") {
    return dialogNode;
  }

  const dialogHost =
    document.querySelector("[data-ds-dialog-host]") ??
    document.querySelector(".ds-app-shell") ??
    document.body;

  return createPortal(dialogNode, dialogHost);
}
